/**
 * Work Queue Pane
 * Left pane displaying the issue list
 */

import { makeStyles, tokens, Text, Caption1 } from '@fluentui/react-components'
import type { IssueSummary } from '../../../domain'
import { WorkQueueList } from './WorkQueueList'
import { usePaneStyles } from '../styles/usePaneStyles'

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    margin: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  body: {
    flex: 1,
    overflow: 'auto',
  },
})

export interface WorkQueuePaneProps {
  issues: IssueSummary[]
  loading: boolean
  error: Error | null
  selectedIssueId: string | null
  onSelectIssue: (id: string) => void
  onRetry?: () => void
  onSelectionAnnounce?: (announcement: string) => void
}

export function WorkQueuePane({
  issues,
  loading,
  error,
  selectedIssueId,
  onSelectIssue,
  onRetry,
  onSelectionAnnounce,
}: WorkQueuePaneProps) {
  const paneStyles = usePaneStyles()
  const styles = useStyles()

  return (
    <div className={paneStyles.root} role="region" aria-labelledby="work-queue-title">
      <div className={styles.header}>
        <Text as="h2" id="work-queue-title" weight="semibold" size={400} className={styles.title}>Work Queue</Text>
        <Caption1 className={styles.subtitle}>
          {loading ? 'Loading...' : `${issues.length} issues`}
        </Caption1>
      </div>
      <div className={styles.body}>
        <WorkQueueList
          issues={issues}
          loading={loading}
          error={error}
          selectedIssueId={selectedIssueId}
          onSelectIssue={onSelectIssue}
          onRetry={onRetry}
          onSelectionAnnounce={onSelectionAnnounce}
        />
      </div>
    </div>
  )
}
